'use client'

import { useEffect, useRef, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useClientSideOnly } from '@marketplace-web/shared/ui-helpers'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import useHelpCenterSessionTracking from '@marketplace-web/domain/help-center-session-tracking'

import { viewFaqEntry } from '_libs/common/event-tracker/events'
import { getFaqDataFromUrl } from '_libs/utils/faq'
import { AccessChannel } from 'constants/access-channel'
import { Screen } from 'constants/tracking/screens'
import { TrackingChannel } from 'constants/tracking/tracking-channels'
import { getInfoBox } from 'data/api/get-info-box'
import { getTransaction } from 'data/api/get-transaction'
import {
  transformHelpCenterFaqEntryChildren,
  transformHelpCenterTransactionResponse,
} from 'data/transformers/help-center'
import { FaqEntryModel } from 'types/models/faq-entry'

import { helpcenterTransactionScreenView } from '@marketplace-web/domain/help-center-home-data'

import HelpCenterFaqEntry from '../HelpCenterFaqEntry'

type Props = {
  faqEntityId: string
  transactionId?: string
  faqEntry: FaqEntryModel
}

const HelpCenterFaqEntryContainer = ({ faqEntityId, transactionId, faqEntry }: Props) => {
  const isClientSideRenderReady = useClientSideOnly()
  const faqContentRef = useRef<HTMLDivElement>(null)
  const [body, setBody] = useState(faqEntry.body)

  const { relativeUrl, searchParams } = useLocation()
  const { track } = useTracking()

  const { access_channel, transaction_id, ch } = searchParams

  const faqEntryParentId = getFaqDataFromUrl(relativeUrl)?.faqEntryParentId

  const defaultTrackingChannel = transactionId
    ? TrackingChannel.Transaction
    : TrackingChannel.HelpCenter
  const deafaultAccessChannel = transactionId ? AccessChannel.HcTransaction : AccessChannel.HcTopics
  const accessChannel = typeof access_channel === 'string' ? access_channel : deafaultAccessChannel
  const trackingChannel = typeof ch === 'string' ? ch : defaultTrackingChannel
  const parsedTransactionId = transactionId || transaction_id?.toString()

  const {
    fetch: fetchInfoBox,
    data: infoboxData,
    isLoading: isInfoBoxLoading,
  } = useFetch(getInfoBox)

  const {
    fetch: fetchTransaction,
    transformedData: transaction = null,
    isLoading: isTransactionLoading,
  } = useFetch(getTransaction, transformHelpCenterTransactionResponse)

  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  useEffect(() => {
    setBody(faqEntry.body)
  }, [faqEntry.body])

  // TODO: After rails controller is removed move this tracker to HelpCenterFaqEntryTrackingEvent
  useEffect(() => {
    if (!helpCenterSessionId) return

    track(
      viewFaqEntry({
        faqEntryId: Number(faqEntityId),
        faqEntryChannel: trackingChannel,
        faqAccessChannel: accessChannel,
        screen: Screen.HelpCenterFaq,
        helpCenterSessionId,
      }),
    )
  }, [faqEntityId, trackingChannel, accessChannel, track, helpCenterSessionId])

  useEffect(() => {
    if (!helpCenterSessionId) return

    if (!transactionId) return

    track(
      helpcenterTransactionScreenView({
        helpCenterSessionId,
        transactionId,
      }),
    )
  }, [track, helpCenterSessionId, transactionId])

  useEffect(() => {
    fetchInfoBox()
  }, [fetchInfoBox])

  useEffect(() => {
    if (transactionId) fetchTransaction({ id: Number(transactionId) })
  }, [transactionId, fetchTransaction])

  const isLoading = !isClientSideRenderReady || isTransactionLoading || isInfoBoxLoading

  useEffect(() => {
    const faqContent = faqContentRef.current

    if (isLoading || !faqContent) return undefined

    const handleTabNavigationClick = (event: MouseEvent) => {
      const element = event.target

      if (!(element instanceof HTMLAnchorElement)) return
      if (!element.classList.contains('nav-link')) return
      if (!element.closest('#faq-container')) return

      const targetTabId = element.getAttribute('data-target')?.replace('#', '')
      event.preventDefault()

      if (!targetTabId) return

      // Attaching event listeners directly on the element doesn't work, for such reason we are adding global event listener on the body
      //   It may be that Event listeners are broken after rerender (haven't tested such case)
      setBody(previousBody => {
        const parser = new DOMParser()
        const dom = parser.parseFromString(previousBody, 'text/html')

        dom
          .querySelectorAll('.tab-content')
          .forEach(tab =>
            tab.id === targetTabId ? tab.classList.add('active') : tab.classList.remove('active'),
          )

        dom
          .querySelectorAll('.nav-link')
          .forEach(navigationLink =>
            navigationLink.getAttribute('data-target') === `#${targetTabId}`
              ? navigationLink.parentElement?.classList.add('active')
              : navigationLink.parentElement?.classList.remove('active'),
          )

        return dom.body.innerHTML
      })
    }

    document.body.addEventListener('click', handleTabNavigationClick)

    return () => {
      document.body.removeEventListener('click', handleTabNavigationClick)
    }
  }, [isLoading])

  const infoBoxBody = infoboxData?.infobox.body || null
  const faqQuestions = faqEntry
    ? transformHelpCenterFaqEntryChildren(
        faqEntry,
        accessChannel,
        faqEntryParentId,
        parsedTransactionId,
      )
    : null

  if (isLoading) {
    return <ContentLoader size={ContentLoader.Size.XLarge} testId="faq-entry-loader" />
  }

  return (
    /*
     * Element ID on this div is necessary for mobile apps to calculate document height when opened in webview.
     * Please DO NOT remove or change the ID without syncing with mobile apps first.
     */
    <div ref={faqContentRef} id="faq-container" className="u-overflow-auto">
      <HelpCenterFaqEntry
        channel={trackingChannel}
        faqEntryBody={body}
        faqEntryDto={null}
        answerNotFoundId={null}
        faqQuestions={faqQuestions}
        faqRecommendedQuestions={faqEntry.topFaqs}
        faqRelatedQuestions={faqEntry.relatedFaqs}
        infoBoxBody={infoBoxBody}
        transaction={transaction}
        transformedFaqEntry={faqEntry}
        faqAccessChannel={accessChannel}
        faqEntityId={Number(faqEntityId)}
      />
    </div>
  )
}

export default HelpCenterFaqEntryContainer
